/**
 * Timbrica — GIF Speed Engine.
 *
 * Turns a GifCore plan into actual output bytes. Kept separate from
 * gif-core.js so that file stays a dependency-free container reader; this one
 * needs a gifsicle runner, which the page owns and injects.
 *
 * Three tiers, cheapest and most faithful first:
 *
 *   1. retime      Plan keeps every frame. The delay fields are patched in
 *                  place and nothing else is touched — lossless, instant, and
 *                  independent of image size. A 100 MB GIF retimes as fast as
 *                  a 10 KB one because no pixel is read.
 *
 *   2. surgery     Plan drops frames AND the file can be unoptimized into
 *                  full-canvas frames. Surviving frames are copied byte for
 *                  byte, so the palette and pixels are the original encoder's.
 *                  No re-quantisation at all.
 *
 *   3. gifsicle    Plan drops frames but unoptimize did not produce full
 *                  frames. Measured on test-anim.gif: gifsicle -U leaves its
 *                  30 frames at 1/30 full, unchanged, while on
 *                  gif-many-frames.gif the same flag takes 14/120 to 120/120.
 *                  So tier 2 cannot be assumed; here gifsicle performs the
 *                  frame selection itself (it composites correctly whatever
 *                  the internal representation) and we write the timing.
 *
 * In every tier the final act is GifCore.applyRetime writing the exact
 * error-diffused delays. That is deliberate: gifsicle's own `-d` can only
 * express one uniform delay for all frames, which is precisely the defect
 * this tool exists to fix. Letting the encoder own the timing would reintroduce
 * it through the back door.
 */
(function (global) {
    'use strict';

    function ensureCore() {
        if (!global.GifCore) throw new Error('GifSpeedEngine requires gif-core.js');
        return global.GifCore;
    }

    /**
     * @param {Object} o
     *   bytes        {Uint8Array}  source GIF
     *   plan         {Object}      from GifCore.planSpeed
     *   runGifsicle  {Function}    (bytes, args[], pre[]) => Promise<Uint8Array>
     *                              `pre` lands BEFORE the input filename —
     *                              required for -U, which gifsicle accepts but
     *                              silently ignores when it comes after.
     *   optimize     {boolean}     run -O2 on re-muxed output (default true)
     *   onProgress   {Function}    (0..1, stageName)
     * @returns {Promise<{bytes: Uint8Array, tier: string, notes: string[]}>}
     */
    function render(o) {
        var GifCore = ensureCore();
        var notes = [];
        var plan = o.plan;
        var src = o.bytes;
        var progress = o.onProgress || function () {};

        var parsed = GifCore.parse(src);
        if (!parsed) return Promise.reject(new Error('not a GIF'));

        /* ---------- tier 1: pure retime ---------- */
        if (plan.mode === 'retime') {
            if (!GifCore.hasWritableTiming(parsed)) {
                // A frame with no Graphic Control Extension has nowhere to hold
                // a delay. Rare (hand-rolled GIF87a exports); route through
                // gifsicle, which will add the extensions, then write timing.
                notes.push('no_gce_rebuild');
                return o.runGifsicle(src, ['-O2'], [])
                    .then(function (out) {
                        var p = GifCore.parse(out);
                        return finish(GifCore, out, p, plan.delays, 'retime_rebuilt', notes);
                    });
            }
            progress(1, 'retime');
            return Promise.resolve({
                bytes: new Uint8Array(GifCore.applyRetime(src, parsed, plan)),
                tier: 'retime',
                notes: notes
            });
        }

        /* ---------- tier 2: container surgery ---------- */
        progress(0.15, 'unoptimize');
        return o.runGifsicle(src, [], ['-U']).then(function (un) {
            var pun = GifCore.parse(un);
            if (pun && GifCore.allFramesFull(pun) && pun.frames.length === parsed.frames.length) {
                progress(0.55, 'select');
                var picked = new Uint8Array(GifCore.selectFrames(un, pun, plan.keep, plan.delays));
                if (o.optimize === false) {
                    return { bytes: picked, tier: 'surgery', notes: notes };
                }
                // -O2 shrinks the re-muxed file back down. It re-derives frame
                // rects and disposal but does not re-quantise, so the palette
                // survives; timing is re-asserted afterwards regardless.
                progress(0.75, 'optimize');
                return o.runGifsicle(picked, ['-O2'], []).then(function (out) {
                    var p = GifCore.parse(out);
                    return finish(GifCore, out, p, plan.delays, 'surgery', notes);
                });
            }

            /* ---------- tier 3: gifsicle selects ---------- */
            notes.push('unoptimize_ineffective');
            progress(0.4, 'select');
            var args = plan.keep.map(function (i) { return '#' + i; });
            if (o.optimize !== false) args.push('-O2');
            return o.runGifsicle(src, args, ['-U']).then(function (out) {
                var p = GifCore.parse(out);
                return finish(GifCore, out, p, plan.delays, 'gifsicle', notes);
            });
        });
    }

    /**
     * Write the planned delays onto whatever the encoder produced.
     *
     * The encoder is allowed to return a different frame count than we asked
     * for — gifsicle merges frames that turn out identical after compositing.
     * Truncating the delay list to match would quietly shorten the animation,
     * so the surplus time is folded into the last surviving frame instead and
     * the discrepancy is reported in `notes`.
     */
    function finish(GifCore, out, parsedOut, delays, tier, notes) {
        if (!parsedOut) throw new Error('encoder returned an unreadable GIF');
        var n = parsedOut.frames.length;
        var use = delays.slice(0, n);
        if (n !== delays.length) {
            notes.push('frame_count_' + delays.length + '_to_' + n);
            var lost = 0, i;
            for (i = n; i < delays.length; i++) lost += delays[i];
            if (n > 0 && lost > 0) use[n - 1] = use[n - 1] + lost;
        }
        if (!GifCore.hasWritableTiming(parsedOut)) {
            notes.push('timing_not_writable');
            return { bytes: out, tier: tier, notes: notes };
        }
        return {
            bytes: new Uint8Array(GifCore.applyRetime(out, parsedOut, { mode: 'retime', delays: use })),
            tier: tier,
            notes: notes
        };
    }

    global.GifSpeedEngine = { render: render };
})(typeof window !== 'undefined' ? window : globalThis);
